import React, { useState, useEffect } from 'react';
import { ActionConfigPopup } from '../../../actions';
import * as Icons from 'lucide-react';
import { useAppStore } from '../../../../store/useAppStore';
import { fetchAppPagesWithVersions } from '../../../../services/firestoreService';
import { getLayoutSlots } from '../../../../config/layouts';
import { getSlotIcon, getSlotDefinition } from '../../../../config/slotDefinitions';

export const LinkForm = ({ component, onUpdate, layoutId }) => {
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [pages, setPages] = useState([]);
  const { appName } = useAppStore();

  // Get available slots for the current layout
  const allSlots = layoutId ? getLayoutSlots(layoutId) : [];
  // Filter slots to only show those that allow multiple components or have maxComponents > 1
  const availableSlots = allSlots.filter(slot => {
    const slotDef = getSlotDefinition(slot.id);
    return slotDef?.allowMultiple === true || (slotDef?.maxComponents || 1) > 1;
  });
  
  // Find default body slot (prefer 'body', fallback to first available slot)
  const defaultSlotId = availableSlots.find(s => s.id === 'body')?.id || 
                       availableSlots.find(s => s.id === 'content')?.id || 
                       (availableSlots.length > 0 ? availableSlots[0].id : 'body');

  // Load pages for the app
  useEffect(() => {
    const loadPages = async () => {
      if (!appName) return;
      try {
        const fetchedPages = await fetchAppPagesWithVersions(appName);
        setPages(fetchedPages);
      } catch (error) {
        console.error('Failed to load pages:', error);
      }
    };
    loadPages();
  }, [appName]);

  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const { specificAttrs } = component;
  const {
    linkType = 'button',
    label = 'Click Here',
    href = '',
    targetPage = '',
    openInSlot = defaultSlotId,
    clearSlotBeforeLoad = true,
    openInNewTab = false,
    variant = 'primary',
    size = 'medium',
    icon = '',
    iconPosition = 'left',
    fullWidth = false,
    underline = false,
    textColor = '#3B82F6',
    backgroundColor = '#3B82F6',
    hoverColor = '#2563EB',
    action = null
  } = specificAttrs;

  // Open action configuration popup
  const handleConfigureAction = () => {
    setShowActionPopup(true);
  };

  // Save action
  const handleActionSelect = (actionConfig) => {
    handleChange('action', actionConfig);
    setShowActionPopup(false);
  };

  // Available icons
  const availableIcons = [
    '', 'ArrowRight', 'ChevronRight', 'ExternalLink', 'Download', 'Send',
    'Mail', 'Phone', 'Home', 'User', 'Settings', 'Info',
    'Star', 'Heart', 'ShoppingCart', 'Search', 'Menu', 'File'
  ];

  return (
    <div style={{ display: 'grid', gap: '16px', padding: '4px' }}>
      {/* Section 1: Link Type & Behavior */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Link Type & Behavior
        </h4>

        {/* Link Type */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Display As
          </label>
          <select
            value={linkType}
            onChange={(e) => handleChange('linkType', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="button">Button</option>
            <option value="text-link">Text Link</option>
            <option value="icon-button">Icon Button</option>
            <option value="card-link">Card Link</option>
          </select>
        </div>

        {/* Label */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Label Text *
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., Click Here, Learn More"
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px'
            }}
          />
        </div>

        {/* External URL */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            External URL (Optional)
          </label>
          <input
            type="text"
            value={href}
            onChange={(e) => handleChange('href', e.target.value)}
            placeholder="https://example.com"
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px'
            }}
          />
          <p style={{ 
            fontSize: '10px', 
            color: '#6B7280', 
            marginTop: '3px',
            fontStyle: 'italic'
          }}>
            Leave empty to use internal page navigation
          </p>
        </div>

        {/* Target Page (only show if href is empty) */}
        {!href && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Internal Page
              </label>
              <select
                value={targetPage || ''}
                onChange={(e) => handleChange('targetPage', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <option value="">-- Select Page --</option>
                {pages.map(page => (
                  <option key={page.id || page.name} value={page.pageName || page.name || page.id}>
                    {page.pageName || page.name || page.id}
                  </option>
                ))}
              </select>
            </div>

            {/* Open Page In Slot */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Open Page In
              </label>
              {availableSlots.length > 0 ? (
                <>
                  <select
                    value={openInSlot || defaultSlotId}
                    onChange={(e) => handleChange('openInSlot', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '13px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF'
                    }}
                  >
                    {availableSlots.map(slot => {
                      const slotDef = getSlotDefinition(slot.id);
                      const slotIcon = getSlotIcon(slot.id);
                      const isDefault = slot.id === defaultSlotId;
                      return (
                        <option key={slot.id} value={slot.id}>
                          {slotIcon} {slot.name || slot.id} {isDefault ? '(Default)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {(() => {
                    const selectedSlot = availableSlots.find(s => s.id === (openInSlot || defaultSlotId));
                    const isDefault = (openInSlot || defaultSlotId) === defaultSlotId;
                    return (
                      <p style={{ 
                        fontSize: '9px', 
                        color: isDefault ? '#10B981' : '#F59E0B', 
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        {isDefault 
                          ? `✓ Most common - loads page in main content area (${selectedSlot?.name || 'Body'})`
                          : `⚠️ Advanced - replaces ${selectedSlot?.name || 'slot'} content`}
                      </p>
                    );
                  })()}
                </>
              ) : (
                <p style={{ 
                  fontSize: '10px', 
                  color: '#9CA3AF', 
                  fontStyle: 'italic',
                  padding: '8px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '4px'
                }}>
                  No available slots found. Please select a layout first.
                </p>
              )}
            </div>

            {/* Clear Slot Before Loading */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={clearSlotBeforeLoad !== false}
                  onChange={(e) => handleChange('clearSlotBeforeLoad', e.target.checked)}
                />
                <span style={{ color: '#4B5563', fontWeight: '500' }}>Clear Slot Before Loading</span>
              </label>
              <p style={{ 
                fontSize: '9px', 
                color: '#9CA3AF', 
                marginTop: '2px',
                fontStyle: 'italic',
                marginLeft: '22px'
              }}>
                {clearSlotBeforeLoad !== false 
                  ? '✓ Slot will be emptied before loading new page content'
                  : '⚠️ New content will be appended to existing slot content'}
              </p>
            </div>
          </>
        )}

        {/* Open in New Tab */}
        <div>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '12px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={openInNewTab || false}
              onChange={(e) => handleChange('openInNewTab', e.target.checked)}
            />
            <span style={{ color: '#4B5563', fontWeight: '500' }}>Open in New Tab</span>
          </label>
        </div>
      </div>

      {/* Section 2: Style & Appearance */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Style & Appearance
        </h4>

        {/* Button Variant (only for button type) */}
        {linkType === 'button' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Button Style
            </label>
            <select
              value={variant}
              onChange={(e) => handleChange('variant', e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF'
              }}
            >
              <option value="primary">Primary (Filled)</option>
              <option value="secondary">Secondary (Outlined)</option>
              <option value="ghost">Ghost (Text Only)</option>
              <option value="danger">Danger (Red)</option>
              <option value="success">Success (Green)</option>
            </select>
          </div>
        )}

        {/* Size */}
        {(linkType === 'button' || linkType === 'icon-button') && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Size
            </label>
            <select
              value={size}
              onChange={(e) => handleChange('size', e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF'
              }}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        )}

        {/* Icon */}
        {linkType !== 'text-link' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Icon (optional)
            </label>
            <select
              value={icon || ''}
              onChange={(e) => handleChange('icon', e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF'
              }}
            >
              {availableIcons.map(iconName => (
                <option key={iconName || 'none'} value={iconName}>
                  {iconName || 'No Icon'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Icon Position (only if icon is selected and not icon-button) */}
        {icon && linkType === 'button' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Icon Position
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  name="iconPosition"
                  value="left"
                  checked={iconPosition === 'left'}
                  onChange={(e) => handleChange('iconPosition', e.target.value)}
                />
                Left
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  name="iconPosition"
                  value="right"
                  checked={iconPosition === 'right'}
                  onChange={(e) => handleChange('iconPosition', e.target.value)}
                />
                Right
              </label>
            </div>
          </div>
        )}

        {/* Full Width (for buttons) */}
        {linkType === 'button' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={fullWidth || false}
                onChange={(e) => handleChange('fullWidth', e.target.checked)}
              />
              <span style={{ color: '#4B5563', fontWeight: '500' }}>Full Width</span>
            </label>
          </div>
        )}

        {/* Underline (for text links) */}
        {linkType === 'text-link' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={underline || false}
                onChange={(e) => handleChange('underline', e.target.checked)}
              />
              <span style={{ color: '#4B5563', fontWeight: '500' }}>Underline</span>
            </label>
          </div>
        )}
      </div>

      {/* Section 3: Colors */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Colors
        </h4>

        {/* Text Color (for text links and ghost buttons) */}
        {(linkType === 'text-link' || variant === 'ghost' || variant === 'secondary') && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Text Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        )}

        {/* Background Color (for filled buttons) */}
        {linkType === 'button' && variant === 'primary' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Background Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        )}

        {/* Hover Color */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Hover Color
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="color"
              value={hoverColor}
              onChange={(e) => handleChange('hoverColor', e.target.value)}
              style={{
                width: '50px',
                height: '34px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
            <input
              type="text"
              value={hoverColor}
              onChange={(e) => handleChange('hoverColor', e.target.value)}
              style={{
                flex: 1,
                padding: '6px 10px',
                fontSize: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Section 4: Action */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Additional Action
        </h4>

        <button
          onClick={handleConfigureAction}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '13px',
            color: '#FFFFFF',
            backgroundColor: action ? '#10B981' : '#3B82F6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          {action ? '✓ Action Configured' : 'Configure Action'}
        </button>
        {action && (
          <p style={{ 
            fontSize: '11px', 
            color: '#6B7280', 
            marginTop: '6px',
            fontStyle: 'italic'
          }}>
            {action.actionType || 'Action set'}
          </p>
        )}
        <p style={{ 
          fontSize: '10px', 
          color: '#9CA3AF', 
          marginTop: '6px',
          fontStyle: 'italic'
        }}>
          Optional: Add an additional action that triggers when the link is clicked
        </p>
      </div>

      {/* Action Configuration Popup */}
      {showActionPopup && (
        <ActionConfigPopup
          isOpen={showActionPopup}
          onClose={() => setShowActionPopup(false)}
          onActionSelect={handleActionSelect}
        />
      )}
    </div>
  );
};